/**
 * TUVI - CRUD de Ingenieros
 * Gestión simple: nombre y teléfono
 */

import React, { useState, useEffect } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { BACKEND_URL } from '../../config';
import './Ingenieros.css';

const Ingenieros = () => {
  const { success, error } = useNotification();
  const [ingenieros, setIngenieros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: '', telefono: '' });
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    cargarIngenieros();
  }, []);

  const cargarIngenieros = async () => {
    try {
      setLoading(true);
      const data = await client.query(`{ ingenieros { id nombre telefono activo } }`);
      setIngenieros(data.ingenieros || []);
    } catch (err) {
      error('Error al cargar ingenieros');
    } finally {
      setLoading(false);
    }
  };

  const abrirFormulario = (ingeniero = null) => {
    if (ingeniero) {
      setEditando(ingeniero.id);
      setForm({ nombre: ingeniero.nombre, telefono: ingeniero.telefono || '' });
    } else {
      setEditando(null);
      setForm({ nombre: '', telefono: '' });
    }
    setMostrarFormulario(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) {
      error('El nombre es obligatorio');
      return;
    }
    try {
      setGuardando(true);
      const url = `${BACKEND_URL}/api/ingenieros/`;
      const method = editando ? 'PUT' : 'POST';
      const body = editando ? { ...form, id: editando } : form;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        success(data.message);
        setMostrarFormulario(false);
        cargarIngenieros();
      } else {
        error(data.message);
      }
    } catch (err) {
      error('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const toggleActivo = async (ing) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ingenieros/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: ing.id, activo: !ing.activo })
      });
      const data = await res.json();
      if (data.success) {
        success(ing.activo ? 'Ingeniero desactivado' : 'Ingeniero activado');
        cargarIngenieros();
      }
    } catch (err) {
      error('Error al cambiar estado');
    }
  };

  if (loading) {
    return (
      <div className="ing-container">
        <div className="ing-loading"><div className="ing-spinner"></div><p>Cargando...</p></div>
      </div>
    );
  }

  return (
    <div className="ing-container">
      <div className="ing-header">
        <div>
          <h2 className="ing-title">Ingenieros</h2>
          <p className="ing-subtitle">{ingenieros.filter(i => i.activo).length} activos de {ingenieros.length} total</p>
        </div>
        <button className="ing-btn ing-btn-primary" onClick={() => abrirFormulario()}>
          + Nuevo Ingeniero
        </button>
      </div>

      {/* Formulario */}
      {mostrarFormulario && (
        <div className="ing-form-card">
          <h3>{editando ? 'Editar Ingeniero' : 'Nuevo Ingeniero'}</h3>
          <div className="ing-form-grid">
            <div className="ing-form-group">
              <label>Nombre *</label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Nombre completo"
              />
            </div>
            <div className="ing-form-group">
              <label>Teléfono</label>
              <input
                type="text"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="Número de contacto"
              />
            </div>
          </div>
          <div className="ing-form-actions">
            <button className="ing-btn ing-btn-secondary" onClick={() => setMostrarFormulario(false)}>
              Cancelar
            </button>
            <button className="ing-btn ing-btn-primary" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando...' : (editando ? 'Actualizar' : 'Crear')}
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      {ingenieros.length === 0 ? (
        <div className="ing-empty">
          <p>No hay ingenieros registrados</p>
          <button className="ing-btn ing-btn-primary" onClick={() => abrirFormulario()}>
            Crear primer ingeniero
          </button>
        </div>
      ) : (
        <div className="ing-lista">
          <table className="ing-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Teléfono</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {ingenieros.map(ing => (
                <tr key={ing.id} className={!ing.activo ? 'ing-inactivo' : ''}>
                  <td className="ing-nombre">{ing.nombre}</td>
                  <td>{ing.telefono || '—'}</td>
                  <td>
                    <span className={`ing-badge ${ing.activo ? 'ing-badge-activo' : 'ing-badge-inactivo'}`}>
                      {ing.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="ing-acciones">
                    <button className="ing-btn-icon" onClick={() => abrirFormulario(ing)} title="Editar">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      className={`ing-btn-icon ${ing.activo ? 'ing-btn-desactivar' : 'ing-btn-activar'}`}
                      onClick={() => toggleActivo(ing)}
                      title={ing.activo ? 'Desactivar' : 'Activar'}
                    >
                      {ing.activo ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Ingenieros;
